import {Component, EventEmitter, Input, Output, OnInit, HostListener, Optional, Self, ElementRef} from '@angular/core';
import {ControlValueAccessor, NgControl} from '@angular/forms';
import {LocalStoreService} from "../../services";

@Component({
    selector: 'app-drop-number-selector',
    templateUrl: './drop-number-selector.component.html',
    styleUrls: ['./drop-number-selector.component.scss']
})
export class DropNumberSelectorComponent implements OnInit, ControlValueAccessor {

    @Input() value = 12;
    @Input() min = 1;
    @Input() max = 412;
    @Input() label = '';
    @Input() presetValues: number[] = [];
    @Input() showManualInput = false;
    @Input() noManualEdit = false;
    @Input() step = 1;
    @Input() placeholder = '';

    // Reactive forms support (validations only - formControl is injected)
    @Input() validations: any[] = [];

    @Output() valueChange: EventEmitter<number> = new EventEmitter<number>();
    @Output() onChange: EventEmitter<number> = new EventEmitter<number>();

    // Reference to the form control (when used with formControlName)
    public control: any = null;

    private onChangeFn: (value: number) => void = () => {};
    private onTouchedFn: () => void = () => {};
    public disabled = false;

    showDropdown = false;
    showInput = false;
    theme = 'light';
    dropdownOpenUpward = false;

    constructor(
        private localStoreService: LocalStoreService,
        @Optional() @Self() public ngControl: NgControl,
        private elementRef: ElementRef
    ) {
        // Set the value accessor on ngControl if it exists (for reactive forms)
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    ngOnInit() {
        // Initialize theme
        this.theme = this.localStoreService.getStorageValConfig('theme') || 'light';

        // Set preset values
        if (this.presetValues.length === 0) {
            // Use dynamic generation if min/max/step look intentional (not defaults)
            // and the range is reasonable (< 200 values)
            const isCustomRange = (this.min !== 1 || this.max !== 412 || this.step !== 1);
            const valueCount = Math.ceil((this.max - this.min) / this.step) + 1;
            const isReasonableRange = valueCount <= 200;

            if (isCustomRange && isReasonableRange && this.step > 0) {
                this.presetValues = this.generatePresetValues(this.min, this.max, this.step);
            } else {
                // Use default preset values for backward compatibility
                this.presetValues = [6, 8, 10, 12, 14, 16, 18, 21, 24, 28, 32, 36, 42, 48, 56, 64, 72, 80, 96, 104, 120, 144];
            }
        }

        // Get reference to the form control if using reactive forms
        if (this.ngControl && this.ngControl.control) {
            this.control = this.ngControl.control;
        }

        // Debug logging
        console.log('DropNumberSelector Debug:', {
            label: this.label,
            showManualInput: this.showManualInput,
            value: this.value,
            min: this.min,
            max: this.max,
            step: this.step,
            theme: this.theme,
            isDarkTheme: this.isDarkTheme,
            hasControl: !!this.control,
            presetValues: this.presetValues
        });
    }

    private generatePresetValues(min: number, max: number, step: number): number[] {
        const values: number[] = [];
        for (let i = min; i <= max; i += step) {
            // Round to avoid floating point precision issues
            const roundedValue = Math.round(i * 1000) / 1000;
            values.push(roundedValue);
        }
        return values;
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: any) {
        if (!event.target.closest('.drop-number-selector')) {
            this.closeDropdown();
        }
    }

    increment() {
        if (this.disabled) return;
        const currentValue = Number(this.value) || 0;
        const newValue = currentValue + this.step;
        const roundedValue = this.roundToStep(Math.min(this.max, newValue));
        this.updateValue(roundedValue);
    }

    decrement() {
        if (this.disabled) return;
        const currentValue = Number(this.value) || 0;
        const newValue = currentValue - this.step;
        const roundedValue = this.roundToStep(Math.max(this.min, newValue));
        this.updateValue(roundedValue);
    }

    private roundToStep(value: number): number {
        // Determine decimal places from step
        const stepStr = this.step.toString();
        const decimalPlaces = stepStr.includes('.') ? stepStr.split('.')[1].length : 0;
        // Round to avoid floating point precision issues
        return Number(value.toFixed(decimalPlaces));
    }

    openDropdown() {
        // Detect available space and determine dropdown direction
        const element = this.elementRef.nativeElement;
        const rect = element.getBoundingClientRect();
        const dropdownHeight = 250; // max-height of dropdown (shows ~5 items)

        // Find the scrollable parent container
        const scrollParent = this.getScrollParent(element);

        let containerBottom: number;
        let spaceAbove: number;

        if (scrollParent && scrollParent !== document.body) {
            // Use scrollable parent container bounds
            const parentRect = scrollParent.getBoundingClientRect();
            containerBottom = parentRect.bottom;
            spaceAbove = rect.top - parentRect.top;
        } else {
            // Use viewport bounds
            containerBottom = window.innerHeight;
            spaceAbove = rect.top;
        }

        const spaceBelow = containerBottom - rect.bottom;

        // Open upward if there's not enough space below but enough space above
        this.dropdownOpenUpward = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;

        this.showDropdown = true;
    }

    private getScrollParent(element: HTMLElement): HTMLElement | null {
        if (!element) {
            return null;
        }

        let parent = element.parentElement;

        while (parent) {
            const overflowY = window.getComputedStyle(parent).overflowY;
            const isScrollable = overflowY === 'auto' || overflowY === 'scroll';

            if (isScrollable && parent.scrollHeight > parent.clientHeight) {
                return parent;
            }

            parent = parent.parentElement;
        }

        return document.body;
    }

    closeDropdown() {
        this.showDropdown = false;
        this.dropdownOpenUpward = false;
    }

    toggleDropdown() {
        this.showDropdown = !this.showDropdown;
    }

    selectPresetValue(preset: number) {
        this.updateValue(preset);
        this.closeDropdown();
    }

    toggleManualInput() {
        this.showInput = !this.showInput;
    }

    onInputChange(event: any) {
        const value = parseInt(event.target.value, 10);
        if (!isNaN(value) && value >= this.min && value <= this.max) {
            this.updateValue(value);
        }
    }

    onInputBlur(event: any) {
        const value = parseInt(event.target.value, 10);
        if (isNaN(value) || value < this.min) {
            this.updateValue(this.min);
        } else if (value > this.max) {
            this.updateValue(this.max);
        } else {
            this.updateValue(value);
        }
    }

    onInputEnter(event: any) {
        event.target.blur();
        this.closeDropdown();
    }

    private updateValue(newValue: number) {
        const currentValue = Number(this.value) || 0;
        if (newValue !== currentValue) {
            this.value = newValue;
            // Emit for legacy two-way binding
            this.valueChange.emit(this.value);
            this.onChange.emit(this.value);
            // Notify reactive forms
            this.onChangeFn(this.value);
            this.onTouchedFn();
        }
    }

    get isDarkTheme(): boolean {
        return this.theme === 'dark' || this.theme === 'app-theme-dark';
    }

    // ControlValueAccessor implementation
    writeValue(value: number): void {
        if (value !== undefined && value !== null) {
            this.value = value;
        }
    }

    registerOnChange(fn: any): void {
        this.onChangeFn = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouchedFn = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    // Helper to get form control for validation
    get formControl() {
        return this.control;
    }
}
